import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { PersonOutline } from '@mui/icons-material';

// Slide-up transition so the prompt behaves like a bottom sheet on mobile.
const SlideUpTransition = React.forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface CompleteProfilePromptProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  actionLabel?: string;
}

const CompleteProfilePrompt: React.FC<CompleteProfilePromptProps> = ({
  open,
  onClose,
  onComplete,
  actionLabel = 'do this',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={isMobile ? SlideUpTransition : undefined}
      sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
      PaperProps={{
        sx: {
          textAlign: 'center',
          p: { xs: 1, sm: 2 },
          ...(isMobile
            ? {
                m: 0,
                width: '100%',
                maxWidth: '100%',
                borderRadius: '20px 20px 0 0',
                pb: 'env(safe-area-inset-bottom)',
              }
            : { borderRadius: 3 }),
        },
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 2 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            backgroundColor: '#f5f0ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <PersonOutline sx={{ fontSize: 36, color: '#533B7D' }} />
        </Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Complete your profile
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add your sports and skill level to {actionLabel}.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1, flexDirection: 'column', px: 3 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onComplete}
          sx={{
            py: 1.5,
            backgroundColor: '#533B7D',
            '&:hover': { backgroundColor: '#4a337a' },
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 'bold',
          }}
        >
          Complete Profile
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
          }}
        >
          Not now
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteProfilePrompt;
